import React from 'react';
import { ChevronLeft, Close } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  ButtonOwnProps,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { AsyncButton } from './Button/AsyncButton';

export interface ChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  actions?: {
    color?: ButtonOwnProps['color'];
    name: string;
    form?: React.ReactNode;
    action?: () => Promise<void>;
    icon?: JSX.Element;
    variant?: ButtonOwnProps['variant'];
    disabled?: boolean;
  }[];
  children: React.ReactNode;
}

export const ChoiceDialog: React.FC<ChoiceDialogProps> = ({
  children,
  actions,
  open,
  onClose,
  title,
}) => {
  const [page, setPage] = React.useState(-1);
  React.useEffect(() => {
    setPage(-1);
  }, [open]);

  const goHome = () => {
    setPage(-1);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <AppBar variant="outlined" elevation={0} position="sticky">
        <Toolbar sx={{ display: 'flex', alignItems: 'center' }} variant="regular">
          <Box sx={{ justifyContent: 'flex-start', display: 'flex', flex: 1 }}>
            {page !== -1 && (
              <IconButton onClick={goHome}>
                <ChevronLeft />
              </IconButton>
            )}
          </Box>
          <Box sx={{ justifyContent: 'center', display: 'flex', flex: 1, flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              {actions?.[page]?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <IconButton size="small" sx={{ ml: 'auto' }} onClick={onClose}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: 400,
          width: 400,
        }}
      >
        {page === -1 && (
          <Box
            sx={{
              flex: 1,
              flexDirection: 'column',
              overflowY: 'auto',
              flexGrow: 1,
              display: 'flex',
              overflow: 'hidden',
              p: 2,
              pt: 0,
            }}
          >
            {children}
          </Box>
        )}
        {page === -1 && (
          <Box sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'row' }}>
            {actions?.map((a, i) =>
              a.action ? (
                <AsyncButton
                  fullWidth
                  sx={{ ml: 1, mr: 1 }}
                  variant="outlined"
                  key={a.name}
                  enableDefaultPadding
                  onClick={a.action}
                  color={a.color}
                  disabled={a.disabled}
                >
                  {a.name}
                </AsyncButton>
              ) : (
                <Button
                  color={a.color}
                  sx={{ mr: 1, ml: 1 }}
                  fullWidth
                  size="small"
                  variant={a.variant || 'outlined'}
                  onClick={() => setPage(i)}
                  key={a.name}
                  startIcon={a.icon}
                  disabled={a.disabled}
                  disableElevation
                >
                  {a.name}
                </Button>
              ),
            )}
          </Box>
        )}
        {page >= 0 && actions?.[page]?.form}
      </Box>
    </Dialog>
  );
};
